import { z } from 'zod';

/**
 * How money reaches a tailoring shop.
 *
 * Its own module rather than a section of the payments schema, because the booking shape needs
 * it too — an advance is taken at the counter as part of booking — and a shared package with a
 * cycle between two schema files breaks at import time rather than at type-check time.
 *
 * The three-way split below is what a shop tallies at closing. Cash is what should be in the
 * drawer. Digital is what should be in the bank by morning. A CHEQUE is neither: it is a piece
 * of paper in a folder that has not cleared, and folding it into "digital" is how a day book
 * stops matching the passbook. Every report that says "cash versus digital" says it about the
 * first two, and shows cheques beside them.
 */

export const PAYMENT_METHODS = ['cash', 'upi', 'card', 'bank_transfer', 'cheque'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];
export const paymentMethodSchema = z.enum(PAYMENT_METHODS);

/** Money that is in the drawer at closing time. */
export const CASH_PAYMENT_METHODS: readonly PaymentMethod[] = Object.freeze(['cash']);

/** Money that is (or will shortly be) in the bank, with a trail somebody else also holds. */
export const DIGITAL_PAYMENT_METHODS: readonly PaymentMethod[] = Object.freeze([
  'upi',
  'card',
  'bank_transfer',
]);

export function isCashMethod(method: PaymentMethod): boolean {
  return CASH_PAYMENT_METHODS.includes(method);
}

export function isDigitalMethod(method: PaymentMethod): boolean {
  return DIGITAL_PAYMENT_METHODS.includes(method);
}

/** Which column of the day book an entry lands in. */
export function methodBucket(method: PaymentMethod): 'cash' | 'digital' | 'cheque' {
  if (isCashMethod(method)) return 'cash';
  if (isDigitalMethod(method)) return 'digital';
  return 'cheque';
}
